
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

const stats = [
  {
    value: '68%',
    label: 'dos segurados nunca leram a apólice completa',
    description: 'A maioria assina contratos sem entender coberturas, exclusões e franquias.',
    icon: 'ri-file-unknow-line',
    color: 'from-red-500 to-red-700',
    bg: 'bg-red-50',
    border: 'border-red-100'
  },
  {
    value: '1 em 3',
    label: 'sinistros tem cobertura negada ou reduzida',
    description: 'Indenizações recusadas ou parciais por falhas na contratação são mais comuns do que se imagina.',
    icon: 'ri-close-circle-line',
    color: 'from-orange-500 to-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-100'
  },
  {
    value: 'R$ 4,2 bi',
    label: 'em sinistros negados por ano no Brasil',
    description: 'Bilhões em indenizações não pagas anualmente por problemas contratuais evitáveis.',
    icon: 'ri-money-dollar-circle-line',
    color: 'from-amber-500 to-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-100'
  },
  {
    value: '42%',
    label: 'pagam mais caro do que deveriam',
    description: 'Quase metade dos segurados contrata coberturas desnecessárias ou paga prêmios acima do mercado.',
    icon: 'ri-price-tag-3-line',
    color: 'from-yellow-500 to-yellow-700',
    bg: 'bg-yellow-50',
    border: 'border-yellow-100'
  },
  {
    value: '78%',
    label: 'renovam sem revisar as coberturas',
    description: 'A renovação automática é um dos maiores erros: o perfil de risco muda, mas o seguro permanece o mesmo.',
    icon: 'ri-refresh-line',
    color: 'from-rose-500 to-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-100'
  },
  {
    value: '90%',
    label: 'dos problemas são evitáveis com assessoria técnica',
    description: 'A grande maioria dos prejuízos com seguros mal contratados poderia ser evitada com análise profissional.',
    icon: 'ri-shield-check-line',
    color: 'from-teal-500 to-teal-700',
    bg: 'bg-teal-50',
    border: 'border-teal-100'
  }
];

const highlights = [
  {
    icon: 'ri-alarm-warning-line',
    text: 'Seguros mal contratados são a principal causa de litígios entre segurados e seguradoras no Brasil'
  },
  {
    icon: 'ri-building-2-line',
    text: 'Empresas são as mais afetadas: coberturas inadequadas podem comprometer a continuidade do negócio'
  },
  {
    icon: 'ri-time-line',
    text: 'O problema só é descoberto no sinistro — quando já é tarde demais para corrigir'
  }
];

export default function InsuranceStatsSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, visibleItems } = useStaggerAnimation(stats.length);
  const { ref: highlightRef, isVisible: highlightVisible } = useScrollAnimation();

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center max-w-4xl mx-auto mb-12 sm:mb-16 transition-all duration-700 ease-out ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 px-4 py-2 rounded-full mb-6">
            <i className="ri-bar-chart-box-line text-red-600 text-base"></i>
            <span className="text-red-700 font-bold text-xs uppercase tracking-wide">Dados do Mercado de Seguros</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-900 mb-4 sm:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Os números que o mercado{' '}
            <span className="text-red-600">prefere não mostrar</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            Dados do setor de seguros revelam a dimensão real do problema. Milhões de brasileiros acreditam estar protegidos — mas não estão.
          </p>
        </div>

        {/* Stats Grid */}
        <div ref={gridRef as React.RefObject<HTMLDivElement>} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`${stat.bg} rounded-2xl border ${stat.border} p-6 sm:p-8 hover:shadow-xl transition-all duration-500 group ${visibleItems[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform`}>
                  <i className={`${stat.icon} text-xl text-white`}></i>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-petrol-900 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {stat.value}
                </div>
              </div>
              <h3 className="text-base font-bold text-petrol-900 mb-2 leading-snug">
                {stat.label}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {stat.description}
              </p>
            </div>
          ))}
        </div>

        {/* Highlight Bar */}
        <div
          ref={highlightRef as React.RefObject<HTMLDivElement>}
          className={`bg-gradient-to-br from-petrol-900 to-petrol-800 rounded-2xl p-8 sm:p-10 transition-all duration-700 ease-out ${highlightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="text-center mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              O que esses números revelam
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {highlights.map((item, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-10 h-10 bg-action-orange-500/20 border border-action-orange-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-lg text-action-orange-400`}></i>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed pt-1">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
